/**
 * Payout tier resolution — mirrors defaults in frontend PayoutRulesBuilder.jsx
 * so mobile behaviour matches what is saved per venue.
 *
 * Slot logic must stay aligned with `backend/src/utils/payoutSignerSlots.js` (Jest) and
 * `frontend/src/pages/payout-safe/payoutPolicyUtils.js`.
 * The TIERS list and PV1-PV4 defaults are drift-checked by
 * `backend/tests/modules/16-payout-policy-sync.guard.test.js`.
 *
 * Signer model:
 *   mandatorySigners       — roles that MUST always sign (fixed slots)
 *   optionalSigners        — pool of roles staff picks from at capture time
 *   optionalSignaturesRequired — how many from optionalSigners must sign
 *
 * Role identifiers are `staff_roles.role_key` values; `patron` is a reserved
 * sentinel for the player. Display labels come from the staff_roles lookup
 * delivered by the payout-config API, not from this file.
 */

const TIERS = ["PV1", "PV2", "PV3", "PV4"];

const defaultPolicyForTier = (tierId) => {
  const base = (enabled, kyc, occ, minA, maxA, cash, pin, mandatory, optional, optReq, issuer) => ({
    enabled,
    kycRequired: kyc,
    occupationRequired: occ,
    minAmount: minA,
    maxAmount: maxA,
    cashDispensed: cash,
    pinEntryByRoleId: pin,
    mandatorySigners: mandatory,
    optionalSigners: optional,
    optionalSignaturesRequired: optReq,
    issuerRoleId: issuer,
  });
  switch (tierId) {
    case "PV1":
      return base(true, false, false, 0, 2000, "CRT_OR_CASH", null, [], [], 0, "cashier");
    case "PV2":
      return base(true, false, false, 2000, 5000, "CASH_ONLY", null, ["patron"], [], 0, "duty_manager");
    case "PV3":
      return base(true, true, false, 5000, 10000, "NO_CASH", "duty_manager", ["patron"], ["duty_manager", "gaming_manager"], 1, "gaming_manager");
    case "PV4":
    default:
      return base(true, true, false, 10000, null, "NO_CASH", "duty_manager", ["patron"], ["duty_manager", "gaming_manager", "venue_manager"], 2, "gaming_manager");
  }
};

export function ensureVenuePolicy(venue) {
  const policy = { ...(venue.payoutPolicy || {}) };
  TIERS.forEach((t) => {
    const def = defaultPolicyForTier(t);
    const existing = policy[t] || {};
    const merged = { ...def };
    for (const [k, v] of Object.entries(existing)) {
      if (v !== undefined) merged[k] = v;
    }
    delete merged.signerRoleIds;
    delete merged.signaturesRequired;
    delete merged.authorizationChain;
    policy[t] = merged;
  });
  return { ...venue, payoutPolicy: policy };
}

/**
 * Pick highest matching slab (PV4 → PV1) so boundary amounts use the higher tier
 * (e.g. $10,000 → PV4 when PV3 max is $10,000 and PV4 min is $10,000).
 */
export function resolveTierForAmount(amount, payoutPolicy) {
  const ensured = ensureVenuePolicy({ payoutPolicy: payoutPolicy || {} }).payoutPolicy;
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return null;

  for (const tid of ["PV4", "PV3", "PV2", "PV1"]) {
    const p = ensured[tid];
    if (!p || !p.enabled) continue;
    const min = p.minAmount ?? 0;
    const max = p.maxAmount;
    if (num >= min && (max === null || max === undefined || max === "" || num <= max)) {
      return { tierId: tid, policy: p };
    }
  }
  return null;
}

/** Patron sentinel role label (the player is not a staff_roles entry). */
export const PATRON_LABEL = "Player";

/**
 * Build signature capture slots from the mandatory/optional signer model.
 *
 * Mandatory slots — fixed: one per mandatorySigners entry.
 * Optional slots  — picker: optionalSignaturesRequired slots, staff selects
 *                   from the optionalSigners pool at capture time.
 *
 * Each slot:
 *   { key, roleId, label, isMandatory, optionRoleIds }
 *   - isMandatory: true  → fixed role, no picker
 *   - isMandatory: false → roleId is null at init; show picker from optionRoleIds
 */
export function getSignerSlots(policy) {
  if (!policy) return [];
  const mandatory = Array.isArray(policy.mandatorySigners) ? policy.mandatorySigners : [];
  const optional = Array.isArray(policy.optionalSigners) ? policy.optionalSigners : [];
  const optReq = Math.max(0, Number(policy.optionalSignaturesRequired ?? 0));

  const slots = [];

  for (let i = 0; i < mandatory.length; i++) {
    const roleId = mandatory[i];
    slots.push({
      key: `mandatory-${i}-${roleId}`,
      roleId,
      label: roleId,
      isMandatory: true,
      optionRoleIds: [],
    });
  }

  for (let i = 0; i < optReq; i++) {
    slots.push({
      key: `optional-${i}`,
      roleId: null,
      label: "Select signer",
      isMandatory: false,
      optionRoleIds: [...optional],
    });
  }

  return slots;
}

/** Total signatures the capture form will collect for a tier. */
export function totalSignaturesForPolicy(policy) {
  if (!policy) return 0;
  const mandatory = Array.isArray(policy.mandatorySigners) ? policy.mandatorySigners.length : 0;
  const optReq = Math.max(0, Number(policy.optionalSignaturesRequired ?? 0));
  return mandatory + optReq;
}
